import eventBus from "@sd-utility/eventbus";
import { PageType, parsePLPURL } from "@tvg/product-listing-url";
import { getSEOMetadata } from "client/api/amplience";
import { captureError } from "client/utils/apm";
import { RESULTS_UPDATE_EVENT } from "client/utils/consts";
import { getSEOMetadataDeliveryKey } from "common/utils/amplience";
import { generatePageTitle } from "common/utils/seo/seo-title";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AmpSEOMetadata } from "types/amplience";
import { Brand } from "types/common";
import { SearchAPIData } from "types/search-api";
import useGlobalConfig from "./GlobalConfig";

export interface SEOConfig {
  overrides?: AmpSEOMetadata;
  fallback?: SearchAPIData | null;
  unfilteredFacet?: string | undefined;
  // the path the current value describes. The breadcrumb parses it for the category, so it has to
  // travel with the response rather than staying in pathRef, which nothing outside this file reads
  path?: string;
}

const SEOConfigContext = createContext<SEOConfig>({} as SEOConfig);
SEOConfigContext.displayName = "SEOConfigContext";

// the facet has to be re-read from every URL the shopper lands on; a malformed path just means no facet
const parseUnfilteredFacet = (path: string, brand: Brand): string | undefined => {
  try {
    return parsePLPURL(path, brand).unfilteredFacet;
  } catch {
    return undefined;
  }
};

const handleCanoncialUpdate = (eventData: { path: string }, overrideUrl?: string) => {
  const carnoncialEl = document.querySelector('link[rel="canonical"]');
  const locationOrigin = window.location.origin;
  const newPath = eventData?.path;

  if (!newPath || !carnoncialEl) return;

  carnoncialEl.setAttribute("href", overrideUrl ?? `${locationOrigin}${newPath}`);
};

export const SEOConfigProvider: React.FC<{
  value: SEOConfig;
  initPath: string;
  unfilteredFacet?: string | undefined;
  children?: React.ReactNode;
}> = ({ value: initValue, initPath, unfilteredFacet, children, ...props }) => {
  const [value, setValue] = useState({ ...initValue, unfilteredFacet, path: initPath });
  const pathRef = useRef(initPath);
  const titleRef = useRef("");
  const seqRef = useRef(0);
  const { brand, pageType } = useGlobalConfig();

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const subscription = eventBus.subscribe(RESULTS_UPDATE_EVENT, (e) => {
      clearTimeout(timeout);

      // Adding timeout to deal with double render causing pathRef to be overriden during process
      timeout = setTimeout(async () => {
        const url = new URL(e.path, location.origin);
        const pageParm = new URLSearchParams(window.location.search)?.get("page");

        handleCanoncialUpdate(e);

        if (url.pathname === pathRef.current) {
          let currentTitle = document.title;
          const hasPageNumber = currentTitle.match(/Page \d/);

          if (hasPageNumber) {
            currentTitle = currentTitle.replace(/Page \d/, "");
          }

          if (pageParm) {
            currentTitle = `${currentTitle} Page ${pageParm}`;
          }

          document.title = `${currentTitle}`;
          return;
        }

        // claimed after the early return so a same-path event cannot cancel a different-path update
        const seq = ++seqRef.current;
        pathRef.current = url.pathname;
        // keep the query string: the breadcrumb re-parses this path and ?uf= only travels there, so
        // the facet follows the shopper instead of staying whatever it was at first render
        const path = url.pathname + url.search;
        const eventFacet = parseUnfilteredFacet(path, brand);
        // the crumb and heading render from the response straight away rather than waiting a CDN
        // round-trip; the metadata patches in below once it lands
        setValue({ overrides: undefined, fallback: e.response, unfilteredFacet: eventFacet, path });

        const deliveryKey = getSEOMetadataDeliveryKey(e.brand, e.path);
        let res: AmpSEOMetadata | undefined;
        try {
          res = await getSEOMetadata(deliveryKey);
        } catch (error) {
          captureError(error);
          res = undefined;
        }
        // a newer event went through while we awaited - its state wins
        if (seq !== seqRef.current) return;

        titleRef.current = res?.title || "";
        if (res) {
          setValue({ overrides: res, fallback: e.response, unfilteredFacet: eventFacet, path });
          handleCanoncialUpdate(e, res.canonicalUrl);
        }

        if (pageType === PageType.Search) return;

        const newTitle =
          (res?.title && `${res?.title}${pageParm ? " Page ${pageParm}" : ""} `) ||
          generatePageTitle({
            overrides: res,
            fallback: e.response,
            brand,
            plpurl: parsePLPURL(url.toString(), brand),
            pageType,
          });

        if (newTitle && document.title !== newTitle) {
          document.title = newTitle;
        }
      }, 0);
    });

    return () => {
      clearTimeout(timeout); // Cleanup any remaining timer
      subscription.unsubscribe(); // Unsubscribe on unmount or brand change
    };
  }, [brand]);

  return (
    <SEOConfigContext.Provider {...props} value={value}>
      {children}
    </SEOConfigContext.Provider>
  );
};

const useSEOConfig = () => useContext(SEOConfigContext);
export default useSEOConfig;
